<script setup lang="ts">
/** Aura Incorporation — First Board of Directors' Resolutions in Writing (신버전: 결의형·s.41A·비서선임). */
import { computed } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill } from './cdd/useCddFill';
import { cafRowIds } from './cdd/cafRows';
import './allotDoc.css';

const fill = useCddFill();

/**
 * 주주 배정표(Allotment) 행 = Client Acceptance 주주 행.
 *   이름·주식수 칸은 CAF 와 **같은 키**(sh{i}_shareholder_name / _number_of_shares)라 여기서
 *   고치면 CAF·Constitution·Share Application 에도 반영되고 반대도 마찬가지다.
 *   주권 번호·납입액(subscription price)은 이 문서 전용 입력.
 * (템플릿 [[if shareholders]] 반복행이 shareholders 배열을 펼친다)
 */
const allottees = computed(() =>
  cafRowIds(fill.values, 'sh', 'shareholder_name').map((i) => ({
    idx: i,
    name: String(fill.values[`sh${i}_shareholder_name`] ?? '').trim() || `Shareholder ${i}`,
  })),
);

/**
 * 이사 전원 — FIRST DIRECTORS 문구([[first_director_names]])와 서명표([[if directors]] 반복행).
 *   서명표 이름 칸은 CAF 이사 이름(dir{i}_director_name)과 같은 키라 양방향 연동된다.
 *   생성 시 서명자(T-Sign)도 이 이사 목록으로 자동 갱신된다(사용자가 직접 고른 서명자는 유지).
 */
const directorRows = computed(() => cafRowIds(fill.values, 'dir', 'director_name'));
const directorNames = computed(() =>
  directorRows.value
    .map((i) => String(fill.values[`dir${i}_director_name`] ?? '').trim())
    .filter(Boolean),
);
const directorNamesText = computed(() => {
  const n = directorNames.value;
  return n.length > 1 ? `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}` : (n[0] || '');
});
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-meta">Company Registration No.: <CddVarField field-key="company_registration_no" label="Company Registration No." /></div>
      <div class="doc-meta italic">(Incorporated in Singapore) (the "Company")</div>
      <div class="doc-lbl-title" style="margin-top:8px">FIRST BOARD OF DIRECTORS' RESOLUTIONS IN WRITING</div>
      <div class="doc-meta">PASSED PURSUANT TO THE COMPANY'S CONSTITUTION</div>
    </div>

    <p class="doc-sec-title" style="text-align:left">INCORPORATION</p>
    <p class="doc-p">
      RESOLVED that it be noted that the company was incorporated on
      <CddVarField field-key="date_of_incorporation" label="Date of Incorporation" /> (&ldquo;date of incorporation&rdquo;) and that
      the Registration Number assigned to it is <CddVarField field-key="company_registration_no" label="Company Registration No." />.
    </p>

    <p class="doc-sec-title" style="text-align:left">FIRST DIRECTORS</p>
    <p class="doc-p">
      RESOLVED that the appointment of
      <b v-if="directorNamesText">{{ directorNamesText }}</b><span v-else class="allot-empty">(directors from the Client Acceptance form)</span>
      as the first directors of the Company,
      having consented to act as directors, with effect from the date of incorporation, be and are hereby noted.
    </p>

    <p class="doc-sec-title" style="text-align:left">APPOINTMENT OF SECRETARY</p>
    <p class="doc-p">
      RESOLVED that <CddVarField field-key="secretary_name" label="Secretary Name" /> be and is hereby appointed as Secretary of the
      Company with effect from the date of the written consent to act as such and that the said Secretary shall hold such office
      until otherwise resolved or determined.
    </p>

    <p class="doc-sec-title" style="text-align:left">REGISTERED OFFICE</p>
    <p class="doc-p">
      RESOLVED that the registered office of the Company be situated at
      <CddVarField field-key="registered_office_address" label="Registered Office Address" /> with effect from the date of incorporation.
    </p>

    <p class="doc-sec-title" style="text-align:left">ALLOTMENT AND ISSUE OF SHARES</p>
    <p class="doc-p">
      RESOLVED that the signatories to the Constitution be registered as members in respect of the shares which are subscribed in cash, namely:
    </p>
    <table class="cdd-tbl allot-tbl">
      <thead>
        <tr>
          <th>Name of Allottee</th>
          <th>Number of Ordinary Shares Allotted</th>
          <th>Subscription price</th>
          <th>Share Certificate No.</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in allottees" :key="row.idx">
          <td><CddVarField :field-key="`sh${row.idx}_shareholder_name`" :label="`Shareholder ${row.idx} Name`" /></td>
          <td><CddVarField :field-key="`sh${row.idx}_shareholder_number_of_shares`" :label="`Number of Shares — ${row.name}`" /></td>
          <td><CddVarField :field-key="`sh${row.idx}_shareholder_amount_payable`" :label="`Subscription price — ${row.name}`" /></td>
          <td><CddVarField :field-key="`sh${row.idx}_shareholder_share_certificate_no`" :label="`Share Certificate No. — ${row.name}`" /></td>
        </tr>
        <tr v-if="!allottees.length">
          <td colspan="4" class="allot-empty">Enter shareholders in the Client Acceptance form first — they appear here automatically.</td>
        </tr>
      </tbody>
    </table>
    <p class="doc-note link-note">Name and share count are linked with the Client Acceptance form (and Constitution / Share Application) — edit here or there.</p>
    <p class="doc-p">
      Share certificate be issued to the above allottee and that the Common Seal of the Company (if any) be affixed to the share
      certificate in accordance with the provisions of its Constitution.
    </p>

    <p class="doc-sec-title" style="text-align:left">FINANCIAL YEAR</p>
    <p class="doc-p">
      RESOLVED that the first financial period of the Company shall be from
      <CddVarField field-key="date_of_incorporation" label="Date of Incorporation" /> (date of incorporation) to
      <CddVarField field-key="first_accounts_period" label="First Accounts Period" /> and thereafter, the financial year of the
      Company shall end on <CddVarField field-key="financial_year_end" label="Financial Year End" /> of each year until otherwise decided.
    </p>

    <p class="doc-sec-title" style="text-align:left">COMMON SEAL</p>
    <p class="doc-p">
      RESOLVED that pursuant to Section 41A of the Companies Act 1967, the Company has elected not to have a common seal.
    </p>

    <p class="doc-p" style="margin-top:10px"><b>Date:</b> <CddVarField field-key="fbod_dated_on" label="Dated On" /></p>
    <!-- 서명표 = 이사 전원 반복행 (템플릿 [[if directors]][[director_name]] | …… [[/if directors]]) -->
    <table class="cdd-tbl sign-tbl">
      <thead>
        <tr><th>DIRECTORS</th><th>SIGNATURES</th></tr>
      </thead>
      <tbody>
        <tr v-for="i in directorRows" :key="i">
          <td><CddVarField :field-key="`dir${i}_director_name`" :label="`Director ${i} Name`" /></td>
          <td><div class="sign-underline"></div></td>
        </tr>
        <tr v-if="!directorRows.length">
          <td colspan="2" class="allot-empty">Enter directors in the Client Acceptance form first — every director gets a signature row.</td>
        </tr>
      </tbody>
    </table>
    <p class="doc-note link-note">Director names are linked with the Client Acceptance form. Every director listed here is added as a T-Sign signer automatically; add or remove directors in the Client Acceptance form.</p>
  </div>
</template>

<style scoped>
.doc-meta.italic { font-style: italic; }
.doc-note { font-size: 10.5px; color: #555; margin-top: 2px; }
.allot-tbl { margin: 8px 0 12px; }
.allot-empty { color: #777; font-style: italic; text-align: center; padding: 10px 6px; }
.sign-underline { border-top: 1px solid #333; width: 180px; margin-top: 22px; }
.sign-tbl { margin-top: 8px; }
.sign-tbl td { vertical-align: bottom; }
.link-note { margin: 4px 0 8px; font-style: italic; }
</style>
